import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Link,
  withRouter,
} from 'react-router-dom';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../../actions';
import { showConfirmationModal } from '../../actions/App';
import { deleteShare } from '../../actions/Shares';

import { getFormattedDate } from '../../helpers/DateTime';
import {
  canUserViewShareDetails,
  thumbnailPlaceholder,
} from '../../helpers/Share';

import Avatar from '../Avatar';
import DateTime from '../DateTime';
import ShareModalLinkCopy from '../Share/ModalLinkCopy';
import ShareLinkCopy from '../Share/LinkCopy';
import EmptyTableCell from '../EmptyTableCell';

const SharesListItem = ({
  intl,
  client,
  history,
  profile,
  share,
  scope,
  handleEditShare,
  handleDeleteShare,
  afterEditShare,
  loadShares,
  workspaceColor,
}) => {
  const afterDeleteShare = () => {
    loadShares();
  };

  const defaultShareLogo = client.share_logo_url;
  const shareLinkUuid = share.links[0].uuid;
  const shareShortLinkSuffix = share.links[0].short_link_url.suffix;
  const shareShortLinkUrl = `${share.links[0].short_link_url.prefix}${shareShortLinkSuffix}`;
  const shareThumbnail = share.featured_file ? share.featured_file.urls.thumbnail :
    (share.thumbnail || thumbnailPlaceholder);

  return (
    <tr>
      <td className="share-thumbnail file-image">
        <Link to={`/shares/${shareLinkUuid}`} target="_blank" className="list-item-img-wrapper">
          <img
            id={`upload-img-${share.uuid}`}
            className="list-item-img"
            width="100%"
            src={shareThumbnail}
            alt=""
          />
        </Link>
      </td>
      <td className="share-link">
        <ShareLinkCopy
          id={`share-link-${share.uuid}`}
          linkUrl={shareShortLinkUrl}
          text={shareShortLinkSuffix}
          tooltipText={intl.formatMessage({ defaultMessage: 'Click to copy link: {link}' }, { link: shareShortLinkUrl })}
          showTooltip={true}
        />
      </td>
      <td className="share-branding">
        <div>
          <div className="share-branding-box" style={{ backgroundColor: share.color ? share.color : workspaceColor }} />
          <img src={share.logo_url || defaultShareLogo} alt="Share logo" className="list-item-img" />
        </div>
      </td>
      {(profile.is_admin || scope === 'to_user') && (
      <th scope="row" className="share-user-name">
        <div className="share-user-name-wrapper">
          <Avatar user={share.user} expandable={true} id={shareLinkUuid} />
        </div>
      </th>
      )}
      <td className="share-date">
        <DateTime
          id={`share-created-${share.uuid}`}
          dateTime={share.created_at}
        />
      </td>
      <td className="share-collection">
        {share.top_collection ? (
          <Link to={`/collections/${share.top_collection.uuid}`} target="_blank">
            {share.top_collection.name}
          </Link>
        ) : <EmptyTableCell />}
      </td>
      <td className="share-files">
        <Link to={`/shares/${shareLinkUuid}`} target="_blank">
          {share.files ? share.files.length : share.files_count}
        </Link>
      </td>
      {(profile.is_admin || scope === 'by_user') && (
      <td className="share-links">
        {share.views_count}
      </td>
      )}
      <td className="share-attributes">
        <ul>
          {share.links.filter(l => (!l.shared_user && !l.shared_email)).length > 0 && (
          <>
            {share.expires_at && (
            <li className="active">
              <span id={`share-expired-${share.uuid}`}>
                <i className="far fa-timer" />
              </span>
              <UncontrolledTooltip placement="bottom" target={`share-expired-${share.uuid}`}>
                {intl.formatMessage({ defaultMessage: 'Expires: {date}' }, { date: getFormattedDate(share.expires_at) })}
              </UncontrolledTooltip>
            </li>
            )}
            {share.include_future_files && (
            <li className="active">
              <span id={`share-future-files-${share.uuid}`}>
                <i className="far fa-crystal-ball" />
              </span>
              <UncontrolledTooltip placement="bottom" target={`share-future-files-${share.uuid}`}>
                {intl.formatMessage({ defaultMessage: 'This is an updatable share link' })}
              </UncontrolledTooltip>
            </li>
            )}
            <li className={share.login_required ? 'active' : ''}>
              <span id={`share-login-${share.uuid}`}>
                {share.login_required ? (
                  <i className="far fa-user" />
                ) : (
                  <i className="far fa-user-slash" />
                )}
              </span>
              <UncontrolledTooltip placement="bottom" target={`share-login-${share.uuid}`}>
                {share.login_required ? intl.formatMessage({ defaultMessage: 'Login required' }) : intl.formatMessage({ defaultMessage: 'Login not required' })}
              </UncontrolledTooltip>
            </li>
          </>
          )}
          <li className={share.allow_download ? 'active' : ''}>
            <span id={`share-download-${share.uuid}`}>
              <i className="far fa-download" />
            </span>
            <UncontrolledTooltip placement="bottom" target={`share-download-${share.uuid}`}>
              {share.allow_download ? intl.formatMessage({ defaultMessage: 'Downloads allowed' }) : intl.formatMessage({ defaultMessage: 'Downloads not allowed' })}
            </UncontrolledTooltip>
          </li>
          {share.require_password && (
          <li className="active">
            <span id={`share-password-${share.uuid}`}>
              <i className="far fa-lock" />
            </span>
            <UncontrolledTooltip placement="bottom" target={`share-password-${share.uuid}`}>
              {intl.formatMessage({ defaultMessage: 'Password required' })}
            </UncontrolledTooltip>
          </li>
          )}
          {share.require_email && (
          <li className="active">
            <span id={`share-email-${share.uuid}`}>
              <i className="far fa-envelope" />
            </span>
            <UncontrolledTooltip placement="bottom" target={`share-email-${share.uuid}`}>
              {intl.formatMessage({ defaultMessage: 'Email required to download' })}
            </UncontrolledTooltip>
          </li>
          )}
        </ul>
      </td>
      <td className="action-more">
        <UncontrolledDropdown>
          <DropdownToggle nav className="options">
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu
            container="body"
            className="dropdown-menu-action-more"
            right
          >
            <DropdownItem className="copy-link-dropdown-item">
              <ShareModalLinkCopy
                linkUrl={shareShortLinkUrl}
                text={intl.formatMessage({ defaultMessage: 'Copy link' })}
                showTooltip={false}
              />
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                window.open(`/shares/${shareLinkUuid}`);
              }}
            >
              <i className="fas fa-file" />
              {intl.formatMessage({ defaultMessage: 'View' })}
            </DropdownItem>
            {canUserViewShareDetails() && (
              <DropdownItem
                onClick={() => {
                  history.push(`/share-links/${shareLinkUuid}/details`);
                }}
              >
                <i className="fas fa-circle-info" />
                {intl.formatMessage({ defaultMessage: 'Details' })}
              </DropdownItem>
            )}
            {(profile.is_admin || scope === 'by_user') && (
            <>
              <DropdownItem
                onClick={() => {
                  handleEditShare(share, afterEditShare);
                }}
              >
                <i className="fas fa-edit" />
                {intl.formatMessage({ defaultMessage: 'Edit' })}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                onClick={() => {
                  handleDeleteShare(share, () => afterDeleteShare());
                }}
              >
                <i className="fas fa-trash" />
                {intl.formatMessage({ defaultMessage: 'Delete' })}
              </DropdownItem>
            </>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </td>
    </tr>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  workspaceColor: state.App.settings.color,
  client: state.Auth.profile?.client,
});

const mapDispatchToProps = (dispatch, props) => ({
  handleEditShare: (share, callback) => {
    dispatch(showModal('ShareModal', {
      className: 'modal-share modal-share-file',
      share,
      afterUpdateShare: callback,
    }));
  },
  handleDeleteShare: (share, callback) => {
    const { intl } = props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Are you sure you want to delete this share? Doing this will not delete the shared files, but the share will be permanently removed and all recipients will lose access to shared files.' }),
      onConfirm: () => (
        dispatch(deleteShare(share.uuid)).then(() => {
          callback(share);
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  },
});

SharesListItem.propTypes = {
  intl: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  share: PropTypes.object.isRequired,
  scope: PropTypes.string.isRequired,
  loadShares: PropTypes.func.isRequired,
  handleEditShare: PropTypes.func.isRequired,
  handleDeleteShare: PropTypes.func.isRequired,
  afterEditShare: PropTypes.func.isRequired,
  workspaceColor: PropTypes.string.isRequired,
};

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(SharesListItem)));
